<template>
  <!-- Review Modal using shared ProspectDetailModal -->
  <ProspectDetailModal
    :open="!!selectedProspect"
    :prospect="selectedProspect"
    :teams="teams"
    :responsible-team-id="selectedProspect?.responsible_team_id"
    :can-edit-team="true"
    :on-update-team="onUpdateTeam"
    @close="handleCloseRequest"
    @prospect-updated="$emit('prospect-updated', $event)"
  >
    <!-- Reassessment banner -->
    <div
      v-if="selectedProspect?.reassess_comment"
      class="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
    >
      <div class="flex items-start gap-2">
        <ArrowUturnLeftIcon class="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p class="text-sm font-medium text-amber-800 dark:text-amber-300">Herbeoordeling</p>
          <p class="text-sm text-amber-700 dark:text-amber-400 mt-1">
            {{ selectedProspect.reassess_comment.replace('Opnieuw beoordelen: ', '') }}
          </p>
          <p class="text-xs text-amber-600 dark:text-amber-500 mt-1">
            Door {{ selectedProspect.reassessed_by_name }} op
            {{ formatDateTime(selectedProspect.reassessed_at) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Eerdere feedback historie -->
    <WorkflowFeedbackHistory
      v-if="selectedProspect"
      :prospect-id="selectedProspect.id"
      class="mb-4"
    />

    <!-- Concept banner (local draft) -->
    <div
      v-if="hasActiveConcept"
      class="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800"
    >
      <div class="flex items-start gap-2">
        <BookmarkIcon class="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div class="flex-1">
          <p class="text-sm font-medium text-blue-800 dark:text-blue-300">
            {{ conceptLoaded ? 'Concept geladen' : 'Concept opgeslagen' }}
          </p>
          <p class="text-sm text-blue-700 dark:text-blue-400 mt-1">
            {{
              conceptLoaded
                ? 'Je eerdere selecties zijn hersteld.'
                : 'Je selecties zijn lokaal opgeslagen.'
            }}
            <span v-if="conceptSavedAt">Opgeslagen op {{ formatDateTime(conceptSavedAt) }}.</span>
          </p>
        </div>
        <button
          @click="$emit('discard-concept')"
          class="text-xs font-medium px-2 py-1 rounded border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 whitespace-nowrap"
        >
          Verwijder concept
        </button>
      </div>
    </div>

    <!-- Preferences with Employee Selection -->
    <div class="mb-6">
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">
          Medewerker Selectie per Rol
        </h4>
        <div
          v-if="!allRolesHaveSelection"
          class="text-xs text-amber-600 dark:text-amber-400 flex items-center"
        >
          <ExclamationTriangleIcon class="h-4 w-4 mr-1" />
          Selecteer voor elke rol een medewerker
        </div>
      </div>

      <div v-if="loadingPreferences" class="text-center py-4">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
      </div>
      <div
        v-else-if="selectedPreferences.length === 0"
        class="text-center py-4 text-gray-500 dark:text-gray-400"
      >
        Geen voorkeuren gevonden
      </div>
      <div v-else class="space-y-4">
        <PreferenceReviewCard
          v-for="(pref, prefIdx) in selectedPreferences"
          :key="pref.id"
          :preference="pref"
          :prospect="selectedProspect"
          :selection="plSelections[pref.id]"
          :local-proposals="localProposals"
          :comparison-employees="comparisonEmployees[pref.id] || []"
          :employee-map="employeeMap"
          :employee-details="employeeDetails"
          :employee-availability="employeeAvailability"
          :teams="teams"
          :is-expanded="expandedPreferences[prefIdx]"
          :is-availability-expanded="availabilityExpanded[pref.id]"
          :expanded-employee-graphs="employeeGraphExpanded[pref.id] || {}"
          :start-in-edit-mode="pref.id === newlyAddedPrefId"
          @select="selection => $emit('select-employee', pref, selection)"
          @select-comparison="selection => $emit('select-comparison', pref, selection)"
          @add-employee="$emit('add-employee', pref)"
          @remove-comparison="empId => $emit('remove-comparison', pref.id, empId)"
          @toggle-expanded="$emit('toggle-expanded', prefIdx)"
          @toggle-availability="$emit('toggle-availability', pref.id)"
          @toggle-employee-graph="empId => $emit('toggle-employee-graph', pref.id, empId)"
          @preference-updated="$emit('preference-updated')"
          @delete-preference="$emit('delete-preference', $event)"
        />
      </div>

      <!-- Add role button -->
      <div v-if="selectedPreferences.length > 0" class="mt-3">
        <button
          @click="$emit('add-role')"
          :disabled="isAddingRole"
          class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm flex items-center gap-1 disabled:opacity-50"
        >
          <PlusIcon class="h-4 w-4" />
          {{ isAddingRole ? 'Toevoegen...' : 'Rol toevoegen' }}
        </button>
        <p v-if="addRoleError" class="mt-1 text-xs text-red-600 dark:text-red-400">
          {{ addRoleError }}
        </p>
      </div>
    </div>

    <!-- Feedback input -->
    <div class="mb-4">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Feedback (verplicht bij Te Bespreken, Geen Match en Opnieuw Aanvragen)
      </label>
      <textarea
        :value="reviewFeedback"
        @input="$emit('update:reviewFeedback', $event.target.value)"
        rows="3"
        class="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm"
        placeholder="Leg uit waarom dit voorstel besproken moet worden, geen match is, of welke rollen aangepast moeten worden..."
      ></textarea>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-2 sm:flex-row-reverse sm:gap-3">
        <button
          @click="$emit('approve')"
          :disabled="isProcessing || !allRolesHaveSelection"
          class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed w-full sm:w-auto"
          :title="!allRolesHaveSelection ? 'Selecteer eerst voor elke rol een medewerker' : ''"
        >
          {{ isProcessing ? 'Verwerken...' : 'Gematched!' }}
        </button>
        <button
          @click="$emit('reject')"
          :disabled="isProcessing"
          class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-400 w-full sm:w-auto"
        >
          Geen Match
        </button>
        <button
          @click="$emit('discuss')"
          :disabled="isProcessing"
          class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-sm font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:bg-orange-400 w-full sm:w-auto"
        >
          Te Bespreken
        </button>
        <button
          @click="$emit('heraanvraag')"
          :disabled="isProcessing"
          class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:bg-purple-400 w-full sm:w-auto"
        >
          Opnieuw aanvragen
        </button>
        <button
          @click="handleSaveConcept"
          :disabled="isProcessing"
          class="inline-flex justify-center items-center gap-1 rounded-md border border-blue-300 dark:border-blue-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-sm font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 w-full sm:w-auto"
        >
          <BookmarkIcon class="h-4 w-4" />
          Opslaan als concept
        </button>
        <span
          v-if="showSaveConfirmation"
          class="text-xs text-green-600 dark:text-green-400 whitespace-nowrap"
        >
          ✓ Concept opgeslagen
        </span>
        <button
          @click="handleCloseRequest"
          class="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
        >
          Annuleren
        </button>
      </div>
    </template>
  </ProspectDetailModal>

  <!-- Unsaved-changes prompt when closing with local selections -->
  <ConfirmActionModal
    :open="showCloseConfirm"
    title="Concept opslaan?"
    message="Je hebt medewerkers geselecteerd die nog niet zijn opgeslagen als concept. Wil je de selecties opslaan of de wijzigingen niet opslaan?"
    confirm-label="Opslaan als concept"
    cancel-label="Terug"
    secondary-label="Niet opslaan"
    variant="warning"
    @confirm="confirmSaveConceptAndClose"
    @secondary="discardAndClose"
    @cancel="showCloseConfirm = false"
  />
</template>

<script setup>
import { ref, onBeforeUnmount } from 'vue'
import {
  ExclamationTriangleIcon,
  ArrowUturnLeftIcon,
  PlusIcon,
  BookmarkIcon
} from '@heroicons/vue/24/outline'
import ProspectDetailModal from '@/components/common/modals/ProspectDetailModal.vue'
import ConfirmActionModal from '@/components/common/modals/ConfirmActionModal.vue'
import PreferenceReviewCard from '@/components/common/cards/PreferenceReviewCard.vue'
import WorkflowFeedbackHistory from '@/components/common/feedback/WorkflowFeedbackHistory.vue'

const props = defineProps({
  selectedProspect: {
    type: Object,
    default: null
  },
  teams: {
    type: Array,
    required: true
  },
  onUpdateTeam: {
    type: Function,
    required: true
  },
  selectedPreferences: {
    type: Array,
    required: true
  },
  loadingPreferences: {
    type: Boolean,
    required: true
  },
  plSelections: {
    type: Object,
    required: true
  },
  localProposals: {
    type: Array,
    default: () => []
  },
  comparisonEmployees: {
    type: Object,
    required: true
  },
  employeeMap: {
    type: Object,
    required: true
  },
  employeeDetails: {
    type: Object,
    required: true
  },
  employeeAvailability: {
    type: Object,
    required: true
  },
  expandedPreferences: {
    type: Object,
    required: true
  },
  availabilityExpanded: {
    type: Object,
    required: true
  },
  employeeGraphExpanded: {
    type: Object,
    required: true
  },
  newlyAddedPrefId: {
    type: [Number, null],
    default: null
  },
  allRolesHaveSelection: {
    type: Boolean,
    required: true
  },
  isAddingRole: {
    type: Boolean,
    required: true
  },
  addRoleError: {
    type: String,
    default: ''
  },
  reviewFeedback: {
    type: String,
    required: true
  },
  isProcessing: {
    type: Boolean,
    required: true
  },
  hasUnsavedChanges: {
    type: Boolean,
    default: false
  },
  formatDateTime: {
    type: Function,
    required: true
  },
  hasActiveConcept: {
    type: Boolean,
    default: false
  },
  conceptLoaded: {
    type: Boolean,
    default: false
  },
  conceptSavedAt: {
    type: String,
    default: null
  }
})

const emit = defineEmits([
  'close',
  'prospect-updated',
  'select-employee',
  'select-comparison',
  'add-employee',
  'remove-comparison',
  'toggle-expanded',
  'toggle-availability',
  'toggle-employee-graph',
  'preference-updated',
  'delete-preference',
  'add-role',
  'update:reviewFeedback',
  'approve',
  'reject',
  'discuss',
  'heraanvraag',
  'save-concept',
  'discard-concept'
])

// Transient "saved" confirmation after clicking "Opslaan als concept"
const showSaveConfirmation = ref(false)
let saveConfirmationTimer = null

// Unsaved-changes prompt on close
const showCloseConfirm = ref(false)

const handleCloseRequest = () => {
  if (props.hasUnsavedChanges && !props.isProcessing) {
    showCloseConfirm.value = true
  } else {
    emit('close')
  }
}

const confirmSaveConceptAndClose = () => {
  showCloseConfirm.value = false
  emit('save-concept')
  emit('close')
}

const discardAndClose = () => {
  showCloseConfirm.value = false
  emit('close')
}

const handleSaveConcept = () => {
  emit('save-concept')
  showSaveConfirmation.value = true
  if (saveConfirmationTimer) clearTimeout(saveConfirmationTimer)
  saveConfirmationTimer = setTimeout(() => {
    showSaveConfirmation.value = false
  }, 2000)
}

onBeforeUnmount(() => {
  if (saveConfirmationTimer) clearTimeout(saveConfirmationTimer)
})
</script>
